import { HttpParams } from '@angular/common/http';

const oneLine = (strings: TemplateStringsArray, ...values: unknown[]) =>
  String.raw({ raw: strings }, ...values)
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean)
    .join(' ');
import { Inject, Injectable } from '@angular/core';
import { WalklistType } from '../lib/common';
import { ENV, environment, IEnvironment } from 'env/config';
import { forkJoin, Observable, of } from 'rxjs';
import { concatMap, map, switchMap, take } from 'rxjs/operators';
import { Result } from '..';

import { Service } from '../entity';

import { DataService } from '../http/dataService';
import { mapResult, PossibleResult } from '../lib/utils';
import { D2CGeo, D2CGeoServiceAddress } from '../models/d2cgeo.model';
import { Walklist } from '../models/walklist';
import { AddressService } from './address';
import { Repository } from './repository';
import { WalklistService } from './walklist';

const getUniqueValuesRequest = (
  queryParams,
  filterField
) => oneLine`<?xml version="1.0" encoding="UTF-8"?>
<wps:Execute version="1.0.0" service="WPS" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
    xmlns="http://www.opengis.net/wps/1.0.0" xmlns:wfs="http://www.opengis.net/wfs"
    xmlns:wps="http://www.opengis.net/wps/1.0.0" xmlns:ows="http://www.opengis.net/ows/1.1"
    xmlns:gml="http://www.opengis.net/gml" xmlns:ogc="http://www.opengis.net/ogc"
    xmlns:wcs="http://www.opengis.net/wcs/1.1.1" xmlns:xlink="http://www.w3.org/1999/xlink"
    xsi:schemaLocation="http://www.opengis.net/wps/1.0.0 http://schemas.opengis.net/wps/1.0.0/wpsAll.xsd">
    <ows:Identifier>gs:Unique</ows:Identifier>
    <wps:DataInputs>
    <wps:Input>
        <ows:Identifier>features</ows:Identifier>
        <wps:Reference mimeType="text/xml" xlink:href="http://geoserver/wps" method="POST">
        <wps:Body>
        <wps:Execute version="1.0.0" service="WPS" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
            xmlns="http://www.opengis.net/wps/1.0.0" xmlns:wfs="http://www.opengis.net/wfs"
            xmlns:wps="http://www.opengis.net/wps/1.0.0" xmlns:ows="http://www.opengis.net/ows/1.1"
            xmlns:gml="http://www.opengis.net/gml" xmlns:ogc="http://www.opengis.net/ogc"
            xmlns:wcs="http://www.opengis.net/wcs/1.1.1" xmlns:xlink="http://www.w3.org/1999/xlink"
            xsi:schemaLocation="http://www.opengis.net/wps/1.0.0 http://schemas.opengis.net/wps/1.0.0/wpsAll.xsd">
            <ows:Identifier>gs:Query</ows:Identifier>
            <wps:DataInputs>
            <wps:Input>
            <ows:Identifier>features</ows:Identifier>
            <wps:Reference mimeType="text/xml" xlink:href="http://geoserver/wfs" method="POST">
            <wps:Body>
            <wfs:GetFeature service="WFS" version="1.0.0" outputFormat="GML2"
              xmlns:telus="http://telus">
              <wfs:Query typeName="telus:address_extension_gs_view" />
            </wfs:GetFeature>
            </wps:Body>
            </wps:Reference>
            </wps:Input>
            <wps:Input>
            <ows:Identifier>attribute</ows:Identifier>
            <wps:Data>
              <wps:LiteralData>${filterField}</wps:LiteralData>
            </wps:Data>
            </wps:Input>
            <wps:Input>
            <ows:Identifier>filter</ows:Identifier>
            <wps:Data>
            <wps:ComplexData mimeType="text/plain; subtype=cql">
              <![CDATA[${queryParams}]]>
            </wps:ComplexData>
            </wps:Data>
            </wps:Input>
            </wps:DataInputs>
            <wps:ResponseForm>
            <wps:RawDataOutput mimeType="application/json">
              <ows:Identifier>result</ows:Identifier>
            </wps:RawDataOutput>
            </wps:ResponseForm>
        </wps:Execute>
        </wps:Body>
        </wps:Reference>
        </wps:Input>
        <wps:Input>
        <ows:Identifier>attribute</ows:Identifier>
        <wps:Data>
          <wps:LiteralData>${filterField}</wps:LiteralData>
        </wps:Data>
        </wps:Input>
    </wps:DataInputs>
    <wps:ResponseForm>
    <wps:RawDataOutput mimeType="application/json">
      <ows:Identifier>result</ows:Identifier>
    </wps:RawDataOutput>
    </wps:ResponseForm>
</wps:Execute>`;

const getUniqueStreetNameTransformedValuesRequest = (
  queryParams
) => oneLine`<?xml version="1.0" encoding="UTF-8"?>
<wps:Execute version="1.0.0" service="WPS" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
    xmlns="http://www.opengis.net/wps/1.0.0" xmlns:wfs="http://www.opengis.net/wfs"
    xmlns:wps="http://www.opengis.net/wps/1.0.0" xmlns:ows="http://www.opengis.net/ows/1.1"
    xmlns:gml="http://www.opengis.net/gml" xmlns:ogc="http://www.opengis.net/ogc"
    xmlns:wcs="http://www.opengis.net/wcs/1.1.1" xmlns:xlink="http://www.w3.org/1999/xlink"
    xsi:schemaLocation="http://www.opengis.net/wps/1.0.0 http://schemas.opengis.net/wps/1.0.0/wpsAll.xsd">
    <ows:Identifier>gs:Unique</ows:Identifier>
    <wps:DataInputs>
    <wps:Input>
    <ows:Identifier>features</ows:Identifier>
    <wps:Reference mimeType="text/xml" xlink:href="http://geoserver/wps" method="POST">
    <wps:Body>
    <wps:Execute>
    <ows:Identifier>gs:Transform</ows:Identifier>
    <wps:DataInputs>
    <wps:Input>
    <ows:Identifier>features</ows:Identifier>
    <wps:Reference mimeType="text/xml" xlink:href="http://geoserver/wps" method="POST">
    <wps:Body>
    <wps:Execute>
    <ows:Identifier>gs:Query</ows:Identifier>
    <wps:DataInputs>
    <wps:Input>
    <ows:Identifier>features</ows:Identifier>
    <wps:Reference mimeType="text/xml" xlink:href="http://geoserver/wfs"
        method="POST">
      <wps:Body>
        <wfs:GetFeature service="WFS" version="1.0.0"
            outputFormat="GML2" xmlns:telus="http://telus">
            <wfs:Query typeName="telus:address_extension_gs_view" />
        </wfs:GetFeature>
      </wps:Body>
    </wps:Reference>
    </wps:Input>
      <wps:Input>
        <ows:Identifier>filter</ows:Identifier>
        <wps:Data>
          <wps:ComplexData mimeType="text/plain; subtype=cql">
              <![CDATA[${queryParams}]]>
          </wps:ComplexData>
        </wps:Data>
      </wps:Input>
    </wps:DataInputs>
    <wps:ResponseForm>
    <wps:RawDataOutput mimeType="application/json">
        <ows:Identifier>result</ows:Identifier>
    </wps:RawDataOutput>
    </wps:ResponseForm>
    </wps:Execute>
    </wps:Body>
    </wps:Reference>
    </wps:Input>
    <wps:Input>
    <ows:Identifier>transform</ows:Identifier>
    <wps:Data>
      <wps:LiteralData>street_name=strTrim((Concatenate(strDefaultIfBlank(Concatenate(dir_prefix, ' '), ''),
      strDefaultIfBlank(Concatenate(street_type_prefix, ' '), ''),
      strDefaultIfBlank(Concatenate(street_name, ' '), ''),
      strDefaultIfBlank(Concatenate(street_type_suffix, ' '), ''),
      strDefaultIfBlank(Concatenate(dir_suffix, ' '), ''))))
      </wps:LiteralData>
    </wps:Data>
    </wps:Input>
    </wps:DataInputs>
    <wps:ResponseForm>
    <wps:RawDataOutput mimeType="text/xml; subtype=wfs-collection/1.0">
      <ows:Identifier>result</ows:Identifier>
    </wps:RawDataOutput>
    </wps:ResponseForm>
    </wps:Execute>
    </wps:Body>
    </wps:Reference>
    </wps:Input>
    <wps:Input>
    <ows:Identifier>attribute</ows:Identifier>
    <wps:Data>
      <wps:LiteralData>street_name</wps:LiteralData>
    </wps:Data>
</wps:Input>
</wps:DataInputs>
<wps:ResponseForm>
  <wps:RawDataOutput mimeType="application/json">
      <ows:Identifier>result</ows:Identifier>
  </wps:RawDataOutput>
</wps:ResponseForm>
</wps:Execute>`;

export const addressExtensionColumns = Object.freeze({
  unitNumber: 'unit',
  streetNumber: 'formatted_street_number',
  streetName: 'formatted_street_name',
  postalCode: 'postal_code',
  sayb: 'sayb_d2c_decision',
  salesWalklistassigned: 'sales_walklist_id',
  dropWalklistassigned: 'dp_walklist_id',
  // Note: this value does not exist in the DB it is sales or drop
  walklistAssigned: 'walklistAssigned',
  dnk: 'do_not_knock',
  city: 'city',
  premiseType: 'premise_type',
  fsa: 'fsa',
  dpStatus: 'derived_drop_permission_status',
});

@Service({
  model: D2CGeo,
})
@Injectable({ providedIn: 'root' })
export class D2CGeoService extends Repository<D2CGeoServiceAddress> {
  readonly wfsEndpoint = 'telus/wfs';
  readonly owsEndpoint = 'ows';

  constructor(
    @Inject(ENV) private env: IEnvironment,
    dataService: DataService,
    private addressExtension: AddressService,
    private walklist: WalklistService
  ) {
    super({
      client: dataService,
      url: env.api.backend.d2cGeo.endpoint as string,
    });
  }

  getAllByPost<I>(
    data: any,
    queryParams?: any,
    headers?: any
  ): Observable<Result<D2CGeoServiceAddress>> {
    const url = `${this.url}${this.api}`;
    return this.dataService
      .post<PossibleResult<I>>(url, data, headers, queryParams)
      .pipe(
        take(1),
        map((result) =>
          mapResult<I, D2CGeoServiceAddress>(result, this.createFromDTO)
        )
      );
  }

  fetchFilterData(queryParams?): Observable<Result<D2CGeoServiceAddress>> {
    const cqlFilter = this.getQueryForRequest(queryParams);
    const data = {
      ...this.setGeoServerQuery('telus:address_extension_gs_view', cqlFilter),
      count: queryParams.pageSize,
      startIndex: queryParams.startIndex ? queryParams.startIndex : 0,
      sortBy: queryParams.sortBy,
    };

    const parameters = {
      sortBy: queryParams.sortBy,
    };

    const headers = Object.assign(
      {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'Content-Type': 'application/x-www-form-urlencoded',
        // eslint-disable-next-line @typescript-eslint/naming-convention
        Accept: '*/*',
      },
      environment.api.headers.d2cGeo
    );

    const body = new HttpParams({
      fromObject: data,
    });

    this.api = this.wfsEndpoint;
    return this.getAllByPost(body, parameters, headers).pipe(
      take(1),
      // Fetching data from address extension to know the walklist ids
      switchMap((result) => {
        const ids = result.items.map((address) => address.id);

        return forkJoin([
          of(result),
          this.addressExtension.getAll({
            ids: ids.join(),
            limit: this.env.k2View.itemsPerPage,
          }),
        ]);
      }),
      // Fetching data from walklist to get the walklist name
      switchMap((results) => {
        const salesIds = [
          ...results[1].items.map((address) => address.salesWalklistId),
        ];
        const dropIds = [
          ...results[1].items.map((address) => address.dpWalklistId),
        ];
        const walklistIds = [
          ...new Set(salesIds.concat(dropIds).filter((address) => address)),
        ];

        const walklistRequest: Observable<Result<Walklist>> =
          walklistIds.length === 0
            ? (of({
                items: [],
                meta: null,
                links: null,
                source: null,
              }) as Observable<Result<Walklist>>)
            : this.walklist.getAll({ walklistids: walklistIds.join() });

        return forkJoin([of(results[0]), of(results[1]), walklistRequest]);
      }),
      // this is to map the walklist data with the data obtained from geo server to add the correct walklist name
      map((resultWithWalklists) => {
        const addressData = resultWithWalklists[1].items;
        const walklistData = resultWithWalklists[2].items;
        const addressWalklistMapping = {};

        resultWithWalklists[0].items.forEach((address) => {
          const addressWalklist = addressData.find(
            (item) => item.addressId === address.id
          );

          address.createdBy = addressWalklist.createdBy;
          address.dpWalklistId = addressWalklist.dpWalklistId;
          address.salesWalklistId = addressWalklist.salesWalklistId;

          const walklistId =
            queryParams.walklistType === WalklistType.dp ||
            queryParams.walklistType === WalklistType.dropPermission
              ? addressWalklist?.dpWalklistId
                ? addressWalklist?.dpWalklistId
                : ''
              : addressWalklist?.salesWalklistId
              ? addressWalklist?.salesWalklistId
              : '';

          const typeOfWalklist =
            queryParams.walklistType === WalklistType.dp ||
            queryParams.walklistType === WalklistType.dropPermission
              ? 'Drop'
              : 'Sales';

          const { walklistName } =
            walklistData.find((walklist) => walklist.id === walklistId) ?? {};

          addressWalklistMapping[address.id] = {
            walklist: walklistName
              ? `${walklistName}`
              : walklistId !== ''
              ? `Missing ${typeOfWalklist} Walklist ID: ${walklistId}`
              : '',
          };
        });

        resultWithWalklists[0].items.forEach((address) => {
          address.walklistAssigned =
            addressWalklistMapping[address.id]?.walklist;
        });

        return resultWithWalklists[0];
      })
    );
  }

  getUniqueValuesOfQuery(queryParams, fieldNameToFilter): Observable<any[]> {
    const cqlFilter = this.getQueryForRequest(queryParams);

    let columnName = '';
    let isWalklistRequest = false;
    let typeOfWalklist;
    if (
      addressExtensionColumns[fieldNameToFilter] ===
      addressExtensionColumns.walklistAssigned
    ) {
      isWalklistRequest = true;
      typeOfWalklist =
        queryParams.walklistType === WalklistType.dp ||
        queryParams.walklistType === WalklistType.dropPermission
          ? 'Drop'
          : 'Sales';
      columnName =
        queryParams.walklistType === WalklistType.dp ||
        queryParams.walklistType === WalklistType.dropPermission
          ? addressExtensionColumns.dropWalklistassigned
          : addressExtensionColumns.salesWalklistassigned;
    } else {
      columnName = addressExtensionColumns[fieldNameToFilter];
    }

    const body = getUniqueValuesRequest(cqlFilter, columnName);
    const params = {
      strict: true,
      sortedBy: addressExtensionColumns[fieldNameToFilter],
    };

    this.api = this.owsEndpoint;

    const request = this.post(
      body,
      environment.api.headers.d2cGeo,
      params
    ).pipe(
      // eslint-disable-next-line @typescript-eslint/no-shadow
      map((result) =>
        result.features.map((item) => {
          let value: any = '';
          if (item?.properties && item.properties.value) {
            value = item.properties.value;
          } else if (item?.properties && item.properties.value === false) {
            value = false;
          } else {
            value = '';
          }

          return { title: value, value };
        })
      )
    );

    if (isWalklistRequest === false) {
      return request;
    }

    let result;

    return request.pipe(
      take(1),
      concatMap((uniqueValuesResult) => {
        result = uniqueValuesResult;
        const walklistIds = uniqueValuesResult.map((item) => item.value);
        return this.getWalklistNamesForIds(walklistIds);
      }),
      map((walklistResult) => {
        const features = result.map((item) => {
          const walklistData = walklistResult.items;
          const walklistId = item.value;

          if (!walklistId) {
            return { title: '', value: '' };
          }

          const waklistName = walklistData.find(
            (walklist) => walklist.id === walklistId
          )?.walklistName;

          const walklistTitle = waklistName
            ? `${waklistName}`
            : `Missing ${typeOfWalklist} Walklist ID: ${walklistId}`;

          return { title: walklistTitle, value: walklistId };
        });

        return features;
      })
    );
  }

  createFromDTO(item): D2CGeoServiceAddress {
    return D2CGeoServiceAddress.createFromDTO(item.properties, item.id);
  }

  /**
   * Get all values for ids for unique filters by
   *
   * @private
   */
  private getWalklistNamesForIds(walklistIds: string[]) {
    const ids = walklistIds.filter((id) => id !== '' && id !== null);
    return this.walklist.getAll({ walklistids: ids.join() });
  }

  private setGeoServerQuery(typeName: string, cqlFilter: string) {
    return {
      service: 'WFS',
      request: 'GetFeature',
      version: '2.0.0',
      typeName,
      outputformat: 'application/json',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      cql_filter: cqlFilter,
    };
  }

  private getQueryForRequest(queryParams) {
    // eslint-disable-next-line @typescript-eslint/quotes
    const isEmptyParameterInFsa =
      queryParams.fsas !== null && queryParams.fsas.indexOf(`' ',`) !== -1;
    const fsaOrPostalCode =
      queryParams.fsas !== null
        ? ` (fsa IN (${queryParams.fsas}) ${
            isEmptyParameterInFsa ? ' OR fsa IS NULL ) ' : ' ) '
          }`
        : ` (${queryParams.postalCodes})`;
    return `${fsaOrPostalCode}${queryParams.filters}`.replace(/,\s*$/, '');
  }
}
